import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  connectQZTray, getAvailablePrinters, printReceipt, resetQZBlock,
  getSavedPrinter, savePrinter, saveMethod, getSavedMethod, runPrinterDiagnostic,
} from "@/utils/printService";
import { printReceiptSilent, downloadReceiptHTML } from "@/utils/silentPrintService";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;

export function usePrinterSetup() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;

  const [method, setMethod] = useState(() => {
    const saved = getSavedMethod();
    if (saved === "qz") return "qz";
    if (saved === "serial") return "serial";
    if (saved === "usb") return "usb";
    if (saved === "browser") return "browser";
    return "qz";
  });

  // Installer
  const [installerDownloading, setInstallerDownloading] = useState(false);
  const downloadInstaller = async (platform) => {
    setInstallerDownloading(true);
    try {
      const endpoint = platform === "windows" ? "installer-windows" : platform === "mac" ? "installer-mac" : "installer-linux";
      const filename = platform === "windows" ? "kaiso-impressora-v10.bat" : platform === "mac" ? "kaiso-impressora-v10.sh" : "kaiso-impressora-v10.sh";
      const response = await fetch(`${API}/api/qz/${endpoint}?v=10&t=${Date.now()}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      const blob = new Blob([text], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${filename} baixado!`);
    } catch (e) { toast.error("Erro ao baixar instalador: " + e.message); }
    setInstallerDownloading(false);
  };

  // Print stations
  const [printStations, setPrintStations] = useState([]);
  const [savingStations, setSavingStations] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    authAxiosRef.current.get("/restaurant").then(r => {
      const data = r.data?.data || r.data;
      const stations = data?.print_stations || [];
      if (stations.length > 0) setPrintStations(stations);
    }).catch(() => {});
  }, []);

  const addStation = (printers) => {
    if (printers.length === 0) { toast.error("Conecte ao QZ Tray primeiro para ver as impressoras disponiveis"); return; }
    setPrintStations(prev => [...prev, {
      printer_name: printers[0] || "Generic / Text Only",
      alias: "", columns: 38, template: "completo",
      auto_print: false, show_store_name: true, show_order_number: true,
      show_order_channel: true, show_delivery_info: true, show_cif: false, copies: 1,
    }]);
  };
  const removeStation = (idx) => setPrintStations(prev => prev.filter((_, i) => i !== idx));
  const updateStation = (idx, field, value) => setPrintStations(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  const saveStations = async () => {
    setSavingStations(true);
    try {
      await authAxios.patch("/restaurant", { print_stations: printStations });
      toast.success(`${printStations.length} estacao(oes) salva(s)!`);
    } catch (e) { toast.error("Erro ao salvar: " + (e?.response?.data?.detail || e.message)); }
    setSavingStations(false);
  };

  // Browser print
  const [browserTestPrinting, setBrowserTestPrinting] = useState(false);
  const handleTestBrowser = async () => {
    setBrowserTestPrinting(true);
    saveMethod("browser");
    try {
      const testOrder = {
        id: "TEST-BROWSER-" + Date.now(), customer_name: "Teste Impressao Navegador",
        items: [
          { product_name: "Sushi Combo Premium", quantity: 2, unit_price: 24.90 },
          { product_name: "Miso Soup", quantity: 1, unit_price: 4.50, notes: "Sem cebola" },
        ],
        total: 54.30, payment_method: "Dinheiro", created_at: new Date().toISOString(),
        notes: "TESTE - Pode descartar",
      };
      let restData = {};
      try { const r = await authAxios.get("/restaurant"); restData = r.data?.data || r.data; } catch {}
      const opts = { storeName: restData?.name || "KAISO SYSTEM", receiptFooter: "Teste de impressao OK!" };
      const result = await printReceiptSilent(testOrder, opts);
      if (result.success) {
        toast.success("Janela de impressao aberta! Selecione sua impressora e clique Imprimir.");
      } else { toast.error(result.error || "Erro na impressao"); }
    } catch (e) { toast.error("Erro: " + e.message); }
    setBrowserTestPrinting(false);
  };

  const handleDownloadReceipt = () => {
    const testOrder = {
      id: "TEST-DOWNLOAD-" + Date.now(), customer_name: "Teste Download Recibo",
      items: [
        { product_name: "Sushi Combo Premium", quantity: 2, unit_price: 24.90 },
        { product_name: "Miso Soup", quantity: 1, unit_price: 4.50, notes: "Sem cebola" },
      ],
      total: 54.30, payment_method: "Dinheiro", created_at: new Date().toISOString(),
    };
    downloadReceiptHTML(testOrder, { storeName: "KAISO SYSTEM", receiptFooter: "Teste OK!" });
    toast.success("Arquivo baixado! Abra o arquivo e imprima.");
  };

  // QZ Tray
  const [qzStatus, setQzStatus] = useState("unknown");
  const [qzError, setQzError] = useState("");
  const [printers, setPrinters] = useState([]);
  const [testPrinting, setTestPrinting] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState(() => getSavedPrinter());

  const tryConnect = async () => {
    setQzStatus("connecting"); setQzError(""); resetQZBlock();
    try {
      const ok = await connectQZTray({ manual: true });
      if (ok) {
        setQzStatus("connected");
        const p = await getAvailablePrinters(); setPrinters(p);
        const saved = getSavedPrinter();
        if (saved && p.includes(saved)) { setSelectedPrinter(saved); }
        else if (p.length > 0) { setSelectedPrinter(p[0]); }
        toast.success(`QZ Tray conectado! ${p.length} impressora(s)`);
      } else {
        setQzStatus("failed");
        setQzError("QZ Tray nao respondeu. Verifique se esta rodando.");
      }
    } catch (e) { setQzStatus("failed"); setQzError(e?.message || String(e)); }
  };

  const testPrint = async () => {
    if (!selectedPrinter) { toast.error("Selecione uma impressora primeiro!"); return; }
    savePrinter(selectedPrinter); saveMethod("qz");
    setTestPrinting(true);
    try {
      const testOrder = {
        id: "TEST-" + Date.now(), customer_name: "Teste de Impressao",
        items: [
          { product_name: "Sushi Combo Premium", quantity: 2, unit_price: 24.90 },
          { product_name: "Miso Soup", quantity: 1, unit_price: 4.50 },
        ],
        total: 54.30, payment_method: "Dinheiro", created_at: new Date().toISOString(),
        notes: "TESTE - Pode descartar",
      };
      let restData = {};
      try { const r = await authAxios.get("/restaurant"); restData = r.data?.data || r.data; } catch {}
      const result = await printReceipt(testOrder, { printer_name: selectedPrinter, copies: 1 }, restData);
      if (result.success) {
        toast.success(`Impresso via QZ Tray na "${selectedPrinter}"! Sem dialogo do Chrome.`);
      } else { toast.error(result.error || "Erro na impressao. Verifique se a impressora esta ligada."); }
    } catch (e) { toast.error("Erro: " + e.message); }
    setTestPrinting(false);
  };

  // Diagnostic
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagRunning, setDiagRunning] = useState(false);
  const runDiagnostic = async () => {
    setDiagRunning(true);
    try {
      const result = await runPrinterDiagnostic();
      setDiagnostic(result);
      if (result.certEndpoint.ok && result.signEndpoint.ok) {
        toast.success("Diagnostico: certificado e assinatura OK!");
      } else { toast.error("Diagnostico: problema detectado. Veja os detalhes."); }
    } catch (e) { toast.error("Erro no diagnostico: " + e.message); }
    setDiagRunning(false);
  };

  return {
    method, setMethod,
    installerDownloading, downloadInstaller,
    printStations, savingStations, addStation, removeStation, updateStation, saveStations,
    browserTestPrinting, handleTestBrowser, handleDownloadReceipt,
    qzStatus, qzError, printers, testPrinting, selectedPrinter, setSelectedPrinter,
    tryConnect, testPrint,
    diagnostic, diagRunning, runDiagnostic,
  };
}
